import React, { useMemo, useState, useEffect } from 'react';

/**
 * EventFocusDrawer – carousel through venues one at a time.
 * Starts on the venue with the next upcoming event.
 * Shows per-route toggles for the visible venue.
 * Previous/Next buttons to navigate between venues.
 */

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const month = d.toLocaleString('en-US', { month: 'long' });
  const day = d.getDate();
  const suffix = getDaySuffix(day);
  return `${month} ${day}${suffix}`;
}

function getDaySuffix(day) {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function findNextEventIndex(venues) {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  let bestIndex = 0;
  let bestDate = null;

  for (let i = 0; i < venues.length; i++) {
    const venue = venues[i];
    if (!venue || !Array.isArray(venue.match_dates)) continue;
    for (const dateStr of venue.match_dates) {
      if (dateStr < todayStr) continue;
      if (!bestDate || dateStr < bestDate) {
        bestDate = dateStr;
        bestIndex = i;
      }
    }
  }

  return { index: bestIndex, date: bestDate };
}

function getNextDateForVenue(venue) {
  if (!venue || !Array.isArray(venue.match_dates)) return null;
  const todayStr = new Date().toISOString().slice(0, 10);
  const future = venue.match_dates.filter(d => d >= todayStr).sort();
  return future.length > 0 ? future[0] : null;
}

function parseRoutes(corr) {
  if (!corr) return [];
  return String(corr).split(',').map(s => s.trim()).filter(Boolean);
}

const EventFocusDrawer = ({
  venues,
  activeRouteIds,
  onToggleRoute,
  routeColors,
  maxHeight = 200,
  mapRef,
  onOpenPointPopup,
  trayConfig
}) => {
  const safeVenues = useMemo(() => {
    return Array.isArray(venues) ? venues.filter(v => v && v.id) : [];
  }, [venues]);

  const { index: nextEventIndex, date: nextEventDate } = useMemo(() => {
    return findNextEventIndex(safeVenues);
  }, [safeVenues]);

  const [selectedIndex, setSelectedIndex] = useState(nextEventIndex);

  // Reset to next event venue when venues change
  useEffect(() => {
    setSelectedIndex(nextEventIndex);
  }, [nextEventIndex]);

  const onLoadIcon = (trayConfig && typeof trayConfig.on_load_icon === 'string')
    ? trayConfig.on_load_icon
    : '';

  const selectedVenue = safeVenues[selectedIndex] || null;
  const isNextEvent = selectedIndex === nextEventIndex;
  const venueNextDate = selectedVenue ? getNextDateForVenue(selectedVenue) : null;

  const headerMessage = isNextEvent && nextEventDate && selectedVenue
    ? `Next match is at ${selectedVenue.title} on ${formatDate(nextEventDate)}`
    : selectedVenue && venueNextDate
      ? `${selectedVenue.title} — ${formatDate(venueNextDate)}`
      : selectedVenue
        ? selectedVenue.title
        : 'No upcoming events';

  const getRouteColor = (routeId) => {
    if (routeColors && routeColors.has(routeId)) return routeColors.get(routeId);
    return '#666666';
  };

  const isRouteActive = (routeId) => {
    if (!activeRouteIds) return false;
    return activeRouteIds.has(String(routeId));
  };

  const handlePrev = () => {
    if (!safeVenues.length) return;
    setSelectedIndex((prev) => ((prev - 1) + safeVenues.length) % safeVenues.length);
  };

  const handleNext = () => {
    if (!safeVenues.length) return;
    setSelectedIndex((prev) => (prev + 1) % safeVenues.length);
  };

  const handleVenueClick = () => {
    if (!selectedVenue || !mapRef || !mapRef.current) return;
    const lat = Number(selectedVenue.lat);
    const lng = Number(selectedVenue.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    mapRef.current.setView([lat, lng], 14, { animate: true, duration: 0.5 });
    if (typeof onOpenPointPopup === 'function') {
      window.setTimeout(() => onOpenPointPopup(selectedVenue), 350);
    }
  };

  const routes = selectedVenue ? parseRoutes(selectedVenue.cooresponding_route) : [];
  const emoji = selectedVenue && typeof selectedVenue['icon-emoji'] === 'string'
    ? selectedVenue['icon-emoji'].trim() : '';

  const btnStyle = {
    height: '34px',
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #0d6efd',
    background: '#0d6efd',
    color: 'white',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    flex: '0 0 auto'
  };

  return (
    <div id="route-status-drawer" style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderTop: '1px solid #ddd',
      padding: '12px',
      zIndex: 1000,
      maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
      overflowY: 'auto'
    }}>
      {/* Header with venue name and event date */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          paddingBottom: '8px',
          marginBottom: '8px',
          borderBottom: '1px solid #eee',
          cursor: 'pointer'
        }}
        onClick={handleVenueClick}
      >
        {emoji && (
          <span style={{ fontSize: '18px', lineHeight: 1 }}>{emoji}</span>
        )}
        <div style={{
          fontSize: '14px',
          fontWeight: 700,
          color: '#333',
          textAlign: 'center'
        }}>
          {headerMessage}
        </div>
      </div>

      {/* Route toggles for the current venue */}
      {routes.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: routes.length <= 4
            ? `repeat(${routes.length}, minmax(0, 1fr))`
            : 'repeat(auto-fit, minmax(80px, 1fr))',
          gap: '6px',
          marginBottom: '10px'
        }}>
          {routes.map((routeId) => {
            const active = isRouteActive(routeId);
            const color = getRouteColor(routeId);

            return (
              <div
                key={routeId}
                onClick={() => { if (onToggleRoute) onToggleRoute(String(routeId)); }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 8px',
                  backgroundColor: active ? '#f0f7ff' : 'white',
                  borderRadius: '6px',
                  border: '1px solid #eee',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: active ? 1 : 0.65
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '3px',
                    backgroundColor: color,
                    transition: 'background-color 0.2s'
                  }} />
                  <span style={{
                    fontWeight: 700,
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    {routeId}
                  </span>
                </div>

                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '36px',
                  height: '20px',
                  flex: '0 0 36px',
                  marginLeft: '8px'
                }} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={active}
                    onChange={() => { if (onToggleRoute) onToggleRoute(String(routeId)); }}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: active ? color : '#ccc',
                    transition: '0.2s',
                    borderRadius: '999px'
                  }} />
                  <span style={{
                    position: 'absolute',
                    height: '16px',
                    width: '16px',
                    left: active ? '18px' : '2px',
                    top: '2px',
                    backgroundColor: 'white',
                    transition: '0.2s',
                    borderRadius: '50%',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }} />
                </label>
              </div>
            );
          })}
        </div>
      )}

      {/* Previous / Next buttons */}
      {safeVenues.length > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <button type="button" onClick={handlePrev} style={btnStyle} aria-label="Previous venue">
            Previous
          </button>
          <button type="button" onClick={handleNext} style={btnStyle} aria-label="Next venue">
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EventFocusDrawer;
